import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { billingApi } from '../services/billingApi';
import type { BillerSummary, Payer, PracticeDetail as PracticeDetailType } from '../types/billing';
import { StatusChip } from '../components/CredentialingStatusChip';
import { useAuth } from '../contexts/AuthContext';

const PracticeDetail: React.FC = () => {
  const { practiceId } = useParams<{ practiceId: string }>();
  const navigate = useNavigate();
  const { role } = useAuth();
  const [practice, setPractice] = useState<PracticeDetailType | null>(null);
  const [payers, setPayers] = useState<Payer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const canManageBillers = role === 'ADMIN' || role === 'CREDENTIALING_ADMIN';
  const [allBillers, setAllBillers] = useState<BillerSummary[]>([]);
  const [assignedBillers, setAssignedBillers] = useState<BillerSummary[]>([]);
  const [savingBillers, setSavingBillers] = useState(false);

  useEffect(() => {
    if (!practiceId) {
      setError('No practice ID in URL');
      setLoading(false);
      return;
    }

    let cancelled = false;
    Promise.all([billingApi.getPractice(practiceId), billingApi.listPayers()])
      .then(([practiceData, payerData]) => {
        if (!cancelled) {
          setPractice(practiceData);
          setPayers(payerData);
        }
      })
      .catch((err: Error) => {
        if (!cancelled) {
          setError(err.message || 'Failed to load practice');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [practiceId]);

  const loadBillers = useCallback(() => {
    if (!canManageBillers || !practice?.id) return;
    Promise.all([
      billingApi.listBillers(),
      billingApi.listBillersOnPractice(practice.id),
    ]).then(([all, assigned]) => {
      setAllBillers(all);
      setAssignedBillers(assigned);
    });
  }, [canManageBillers, practice?.id]);

  useEffect(() => {
    loadBillers();
  }, [loadBillers]);

  const handleBillerChange = async (_: unknown, newValue: BillerSummary[]) => {
    if (!practice?.id) return;
    const currentIds = new Set(assignedBillers.map((b) => b.id));
    const nextIds = new Set(newValue.map((b) => b.id));
    const toGrant = [...nextIds].filter((id) => !currentIds.has(id));
    const toRevoke = [...currentIds].filter((id) => !nextIds.has(id));

    setSavingBillers(true);
    try {
      await Promise.all([
        ...toGrant.map((id) => billingApi.grantBillerToPractice(practice.id, id)),
        ...toRevoke.map((id) => billingApi.revokeBillerFromPractice(practice.id, id)),
      ]);
      loadBillers();
    } finally {
      setSavingBillers(false);
    }
  };

  const payersById = useMemo(() => new Map(payers.map((p) => [p.id, p])), [payers]);
  const locationsById = useMemo(
    () => new Map((practice?.locations ?? []).map((l) => [l.id, l])),
    [practice],
  );
  const providersById = useMemo(
    () => new Map((practice?.providers ?? []).map((p) => [p.id, p])),
    [practice],
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !practice) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/practices')} sx={{ mb: 2 }}>
          Back to practices
        </Button>
        <Alert severity="error">{error || 'Practice not found'}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/practices')}
        >
          Back to practices
        </Button>
        {canManageBillers && (
          <Button variant="outlined" onClick={() => navigate('/invitations/new')}>
            Send intake invitation
          </Button>
        )}
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="h4">{practice.name}</Typography>
            {practice.billingCustomer && (
              <Chip
                label="Billing customer"
                size="small"
                sx={{ backgroundColor: '#e3f2fd', color: '#0d47a1', fontWeight: 600 }}
              />
            )}
          </Stack>

          {practice.legalName && practice.legalName !== practice.name && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Legal name: {practice.legalName}
            </Typography>
          )}

          <Grid container spacing={2} sx={{ mt: 1 }}>
            {practice.groupNpi && (
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary">
                  Group NPI
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {practice.groupNpi}
                </Typography>
              </Grid>
            )}
            {practice.ein && (
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary">
                  EIN
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {practice.ein}
                </Typography>
              </Grid>
            )}
            {practice.primaryContactEmail && (
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary">
                  Contact email
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {practice.primaryContactEmail}
                </Typography>
              </Grid>
            )}
            {practice.primaryContactPhone && (
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary">
                  Phone
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {practice.primaryContactPhone}
                </Typography>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      <Typography variant="h6" sx={{ mb: 1 }}>
        Locations ({practice.locations.length})
      </Typography>
      {practice.locations.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          No locations recorded.
        </Typography>
      ) : (
        <Paper sx={{ mb: 3 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Label</TableCell>
                  <TableCell>Address</TableCell>
                  <TableCell>City</TableCell>
                  <TableCell>State</TableCell>
                  <TableCell>ZIP</TableCell>
                  <TableCell>County</TableCell>
                  <TableCell>Primary</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {practice.locations.map((loc) => (
                  <TableRow key={loc.id}>
                    <TableCell>{loc.label || '-'}</TableCell>
                    <TableCell>
                      {[loc.street1, loc.street2].filter(Boolean).join(', ') || '-'}
                    </TableCell>
                    <TableCell>{loc.city || '-'}</TableCell>
                    <TableCell>{loc.state || '-'}</TableCell>
                    <TableCell>{loc.zip || '-'}</TableCell>
                    <TableCell>{loc.county || '-'}</TableCell>
                    <TableCell>{loc.isPrimary ? '✓' : ''}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Typography variant="h6" sx={{ mb: 1 }}>
        Providers ({practice.providers.length})
      </Typography>
      {practice.providers.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          No providers recorded.
        </Typography>
      ) : (
        <Paper sx={{ mb: 3 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Specialty</TableCell>
                  <TableCell>NPI</TableCell>
                  <TableCell>CAQH</TableCell>
                  <TableCell>CAQH managed</TableCell>
                  <TableCell>Email</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {practice.providers.map((prov) => (
                  <TableRow key={prov.id}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {[prov.firstName, prov.middleName, prov.lastName].filter(Boolean).join(' ')}
                        {prov.credentials ? `, ${prov.credentials}` : ''}
                      </Typography>
                    </TableCell>
                    <TableCell>{prov.specialty || '-'}</TableCell>
                    <TableCell>{prov.npi || '-'}</TableCell>
                    <TableCell>{prov.caqhNumber || '-'}</TableCell>
                    <TableCell>{prov.caqhManagedByRevmac ? '✓' : ''}</TableCell>
                    <TableCell>{prov.email || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Divider sx={{ my: 3 }} />

      <Typography variant="h6" sx={{ mb: 1 }}>
        Credentialing applications ({practice.applications.length})
      </Typography>
      {practice.applications.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No applications recorded.
        </Typography>
      ) : (
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Provider</TableCell>
                  <TableCell>Payer</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Effective date</TableCell>
                  <TableCell>Biller</TableCell>
                  <TableCell>Last update</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {practice.applications.map((app) => {
                  const provider = app.providerId ? providersById.get(app.providerId) : null;
                  const location = locationsById.get(app.practiceLocationId);
                  const payer = payersById.get(app.payerId);
                  return (
                    <TableRow key={app.id}>
                      <TableCell>
                        {provider
                          ? `${provider.firstName} ${provider.lastName}`
                          : <em>Group-level</em>}
                      </TableCell>
                      <TableCell>{payer?.name ?? app.payerId.slice(0, 8)}</TableCell>
                      <TableCell>
                        {location?.label || location?.city || location?.zip || '-'}
                      </TableCell>
                      <TableCell>
                        <StatusChip
                          state={app.state}
                          waitingOn={app.waitingOn}
                          color={app.color}
                        />
                      </TableCell>
                      <TableCell>{app.effectiveDate || '-'}</TableCell>
                      <TableCell>{app.biller || '-'}</TableCell>
                      <TableCell>
                        {app.updatedAt ? new Date(app.updatedAt).toLocaleDateString() : '-'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {canManageBillers && (
        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Assigned billers</Typography>
          <Autocomplete<BillerSummary, true>
            multiple
            options={allBillers}
            value={assignedBillers}
            onChange={handleBillerChange}
            getOptionLabel={(b) => `${b.firstName} ${b.lastName}`}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            disabled={savingBillers}
            renderTags={(value, getTagProps) =>
              value.map((b, idx) => {
                const { key, ...tagProps } = getTagProps({ index: idx });
                return <Chip key={key} {...tagProps} label={`${b.firstName} ${b.lastName}`} />;
              })
            }
            renderInput={(params) => (
              <TextField {...params} label="Billers" placeholder="Add a biller…" />
            )}
          />
        </Paper>
      )}
    </Box>
  );
};

export default PracticeDetail;
